import { logger } from '@/core/logger';
import { asyncStorage } from '@/core/storage';
import { REGISTRATION_STEP, REGISTRATION_TYPE, useManualRegistrationStore } from '@/modules/onboarding/registration/store/registrationStore';

/**
 * Get the target route for manual registration based on current step
 * This is used by splash screen to determine where to route
 */
export async function getManualRegistrationRoute(): Promise<string | null> {
    const registrationStore = useManualRegistrationStore.getState();

    // Check if manual registration is in progress
    // For new flow: formData exists (before registration API call)
    // For old flow: registrationResponse exists (after registration API call)
    const hasRegistrationData = (registrationStore.formData || registrationStore.registrationResponse) && registrationStore.step;
    if (!hasRegistrationData) {
        return null;
    }

    const step = registrationStore.step;

    switch (step) {
        case REGISTRATION_STEP.FORM:
            // If form data exists but no registration response, user is on form step
            if (registrationStore.formData && !registrationStore.registrationResponse) {
                return '/registration-form';
            }
            // If registration response exists, continue to document capture
            if (registrationStore.registrationResponse) {
                // Check upload status for UAE users to determine passport-only view
                if (registrationStore.registrationType === REGISTRATION_TYPE.EMIRATES_ID) {
                    return await getDocumentViewerRoute();
                }
                return '/document-upload';
            }
            // Otherwise, start registration form
            return '/registration-form';

        case REGISTRATION_STEP.PHONE_OTP:
            logger.info('ManualRegistrationRouting:Routing to phone-otp-inline');
            return '/phone-otp-inline';

        case REGISTRATION_STEP.EMAIL_OTP:
            logger.info('ManualRegistrationRouting:Routing to email-otp-inline');
            return '/email-otp-inline';

        case REGISTRATION_STEP.DOCUMENT_CAPTURE:
            // Check upload status to determine if we should show passport-only view
            if (registrationStore.registrationType === REGISTRATION_TYPE.EMIRATES_ID) {
                return await getDocumentViewerRoute();
            }
            return '/document-upload';

        case REGISTRATION_STEP.DOCUMENT_REVIEW:
            logger.info('ManualRegistrationRouting:Routing to document-review');
            return '/document-review';

        default:
            // Unknown step, default to document viewer if registration response exists
            if (registrationStore.registrationResponse) {
                return '/document-upload';
            }
            // If formData exists but no step, default to form
            if (registrationStore.formData) {
                return '/registration-form';
            }
            return null;
    }
}

/**
 * Get document upload route based on upload status
 * Returns document-upload with appropriate query params, never document-viewer
 * (document-viewer is only for viewing documents from profile, not registration flow)
 */
async function getDocumentViewerRoute(): Promise<string> {
    try {
        const uploadStatusStr = await asyncStorage.getItem('document.upload.status');
        if (uploadStatusStr) {
            const uploadStatus = JSON.parse(uploadStatusStr) as Record<string, { status: string }>;
            const eidFrontUploaded = uploadStatus.emiratesIdFront?.status === 'uploaded';
            const eidBackUploaded = uploadStatus.emiratesIdBack?.status === 'uploaded';
            const passportUploaded = uploadStatus.passport?.status === 'uploaded';

            // If EID is complete but passport is not, show passport-only view
            if (eidFrontUploaded && eidBackUploaded && !passportUploaded) {
                logger.info('ManualRegistrationRouting:Routing to passport-only document upload', {
                    eidFrontUploaded,
                    eidBackUploaded,
                    passportUploaded,
                });
                return '/document-upload?registrationType=passport';
            }
        }
    } catch (error) {
        logger.error('ManualRegistrationRouting:Failed to check upload status', error);
    }
    // Always return document-upload for registration flow, never document-viewer
    return '/document-upload';
}

/**
 * Check if manual registration is ongoing
 * Returns true if formData exists (new flow) OR registrationResponse exists (old flow)
 */
export function isManualRegistrationOngoing(): boolean {
    const registrationStore = useManualRegistrationStore.getState();
    // Check for new flow (formData exists) or old flow (registrationResponse exists)
    return !!(registrationStore.formData || registrationStore.registrationResponse);
}

